import { useContext, useEffect, useMemo, useState } from 'react';
import axiosConn from '../../axiosConn';
import { AuthContext } from '../../context/AuthContext';
import { Tooltip } from '@mui/material';
import { useQueryClient } from 'react-query';

import './cashoutJGKToken.css';
import DisplayPlayerJGK from '../../components/displayPlayerJGK/DisplayPlayerJGK';
import { CircularProgress } from '@material-ui/core';
import HelpOutlineOutlinedIcon from '@mui/icons-material/HelpOutlineOutlined';
import Form from 'react-bootstrap/Form';
import {
  usdJGKDecimalConversionRate,
  usdJGKConversionRate,
  jgkTransactionFee,
} from '../../utils/config';
import { useGetCurrPlayer } from '../../hooks/useGetCurrPlayer';
import { useSuspension } from '../../context/SuspensionContext';
import { useTheme } from '../../context/ThemeContext';

const GIFT_CARD_TIER1 = [
  'visa',
  'mastercard',
  'amazon',
  'walmart',
  'target',
  'apple',
  'app store',
  'itunes',
  'google play',
  'uber',
  'uber eats',
  'starbucks',
  'netflix',
  'doordash',
  'instacart',
  'best buy',
  'home depot',
  'lowe',
  'cvs',
  'walgreens',
];

const getGiftCardTierRank = (productName = '') => {
  const name = productName.toLowerCase();
  const index = GIFT_CARD_TIER1.findIndex((keyword) => name.includes(keyword));
  return index === -1 ? Number.POSITIVE_INFINITY : index;
};

// import Container from 'react-bootstrap/Container';
// import Row from 'react-bootstrap/Row';
// import Col from 'react-bootstrap/Col';
// import ButtonGroup from 'react-bootstrap/ButtonGroup';
// import ToggleButton from 'react-bootstrap/ToggleButton';

export default function CashoutJGKToken({
  showJGKTutorial = false,
  SetShowJGKTutorial = () => {},
  mode = 'all',
  embedded = false,
  onRedeemSuccess,
  onSendSuccess,
}) {
  // const reloadlyApiClientIdProd = process.env.RELOADLY_API_CLIENT_ID_PROD;
  // const reloadlyApiClientSecretProd =
  //   process.env.RELOADLY_API_CLIENT_SECRET_PROD;
  // const reloadlyApiAudienceProd = process.env.RELOADLY_API_AUDIENCE_PROD;

  const { player } = useContext(AuthContext);
  const queryClient = useQueryClient();
  const baseRedeemMode = mode === 'redeem';
  const isSendMode = mode === 'send';
  const showModeToggle = !embedded && mode === 'all';
  const [myFriends, SetMyFriends] = useState([]);
  const [cashoutGiftCard, setCashoutGiftCard] = useState(
    baseRedeemMode || mode === 'all' ? 1 : 0,
  );
  const [sendJGKPlayer, setSendJGKPlayer] = useState(isSendMode ? 1 : 0);
  const [selectedPlayer, setSelectedPlayer] = useState('Choose a Player');
  const [sendJGKToPlayerAmount, setSendJGKToPlayerAmount] = useState(0);
  const [cashoutGiftCardAmount, setCashoutGiftCardAmount] = useState(0);
  const [sendJGKToPlayerMemo, setSendJGKToPlayerMemo] = useState(
    'sharing JGK is caring.',
  );
  const [giftCards, setGiftCards] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [currPlayer, setCurrPlayer] = useState({});
  const [message, setMessage] = useState('');
  const [error, SetError] = useState('');
  const [countryCode, setCountryCode] = useState('US');
  const [reloadlyAccessToken, setReloadlyAccessToken] = useState(
    JSON.parse(localStorage.getItem('reloadlyAccessToken') || null),
  );
  const [, setReloadlyAccessTokenExpiration] = useState(
    JSON.parse(localStorage.getItem('reloadlyAccessTokenExpiration') || null),
  );
  const { isSuspended } = useSuspension();
  const { darkTheme } = useTheme();

  const [thisGiftCard, setThisGiftCard] = useState('');
  const [active, setActive] = useState('');
  const [giftCardUSDAmount, setGiftCardUSDAmount] = useState(0);
  const [giftSearch, setGiftSearch] = useState('');
  const [friendSearch, setFriendSearch] = useState('');
  const [confirmRedeemOpen, setConfirmRedeemOpen] = useState(false);
  const [giftCardsLoading, setGiftCardsLoading] = useState(false);
  const [friendsLoading, setFriendsLoading] = useState(false);
  const [lastSend, setLastSend] = useState(() => {
    if (typeof window === 'undefined') return null;
    const recipient = localStorage.getItem('jgkWalletLastSendRecipient');
    const amount = Number(localStorage.getItem('jgkWalletLastSendAmount'));
    if (!recipient || !Number.isFinite(amount) || amount <= 0) return null;
    return { recipient, amount };
  });
  const [visibleGiftCount, setVisibleGiftCount] = useState(12);
  const [visibleFriendCount, setVisibleFriendCount] = useState(16);
  const isRedeemMode = baseRedeemMode || cashoutGiftCard === 1;

  useEffect(() => {
    setVisibleGiftCount(12);
  }, [giftSearch, cashoutGiftCard]);

  useEffect(() => {
    setVisibleFriendCount(16);
  }, [friendSearch, sendJGKPlayer]);

  // Mode switching between redeem/send
  useEffect(() => {
    if (mode === 'redeem') {
      setCashoutGiftCard(1);
      setSendJGKPlayer(0);
      return;
    }
    if (mode === 'send') {
      setCashoutGiftCard(0);
      setSendJGKPlayer(1);
    }
  }, [mode]);

  // Derive country from IP (legacy client-side path until backend proxy exists).
  useEffect(() => {
    if (!isRedeemMode) return;
    let mounted = true;
    const getData = async () => {
      try {
        const res = await axiosConn.get('https://ipapi.co/json/');
        if (!mounted) return;
        const data = res.data;
        setCountryCode(data?.country_code || 'US');
      } catch (err) {
        if (!mounted) return;
        setCountryCode('US');
      }
    };

    getData();
    return () => {
      mounted = false;
    };
  }, [isRedeemMode]);

  // Acquire Reloadly token client-side (temporary until backend proxy is added).
  useEffect(() => {
    if (!isRedeemMode) return;
    let mounted = true;
    const now = Date.now();
    const cached = JSON.parse(localStorage.getItem('reloadlyAccessToken') || null);
    const cachedExpiry = Number(
      JSON.parse(localStorage.getItem('reloadlyAccessTokenExpiration') || null),
    );
    const freshTokenExists =
      cached && Number.isFinite(cachedExpiry) && now < cachedExpiry * 0.7;
    if (freshTokenExists) {
      setReloadlyAccessToken(cached);
      setReloadlyAccessTokenExpiration(cachedExpiry);
      return;
    }
    const fetchToken = async () => {
      try {
        const res = await axiosConn.post(
          'https://auth.reloadly.com/oauth/token',
          {
            client_id: process.env.REACT_APP_RELOADLY_CLIENT_ID,
            client_secret: process.env.REACT_APP_RELOADLY_CLIENT_SECRET,
            grant_type: 'client_credentials',
            audience: process.env.REACT_APP_RELOADLY_AUDIENCE,
          },
          {
            headers: {
              'Content-Type': 'application/json',
              Accept: 'application/json',
            },
          },
        );
        if (!mounted) return;
        const nextExpiry =
          new Date().getTime() + (res?.data?.expires_in || 0) * 1000;
        setReloadlyAccessToken(res.data);
        setReloadlyAccessTokenExpiration(nextExpiry);
        localStorage.setItem(
          'reloadlyAccessToken',
          JSON.stringify(res.data),
        );
        localStorage.setItem(
          'reloadlyAccessTokenExpiration',
          JSON.stringify(nextExpiry),
        );
      } catch (err) {
        if (!mounted) return;
        SetError('Gift cards are unavailable right now.');
      }
    };
    fetchToken();
    return () => {
      mounted = false;
    };
  }, [isRedeemMode]);

  // Gift card catalog fetch via Reloadly API (legacy client path).
  useEffect(() => {
    if (!isRedeemMode) return;
    if (
      reloadlyAccessToken === null ||
      reloadlyAccessToken === typeof undefined
    ) {
      return;
    }
    let mounted = true;
    const getGiftCardProducts = async () => {
      setGiftCardsLoading(true);
      try {
        const giftCardList = await axiosConn.get(
          `https://giftcards.reloadly.com/products?size=&page=&productName=&countryCode=${countryCode}&includeRange=true&includeFixed=true`,
          {
            headers: {
              Accept: 'application/com.reloadly.giftcards-v1+json',
              Authorization: `Bearer ${reloadlyAccessToken.access_token}`,
            },
          },
        );

        if (!mounted) return;

        setGiftCards(
          giftCardList.data.content
            .sort((a, b) => {
              const rankA = getGiftCardTierRank(a?.productName || '');
              const rankB = getGiftCardTierRank(b?.productName || '');
              if (rankA !== rankB) return rankA - rankB;
              const discountA = Number(a?.discountPercentage || 0);
              const discountB = Number(b?.discountPercentage || 0);
              if (discountB !== discountA) return discountB - discountA;
              const nameA = a?.productName || '';
              const nameB = b?.productName || '';
              return nameA.localeCompare(nameB);
            }),
        );
        setGiftCardsLoading(false);
        SetError('');
      } catch (err) {
        if (!mounted) return;
        setGiftCardsLoading(false);
        SetError('Gift cards are unavailable right now.');
      }
    };
    getGiftCardProducts();
    return () => {
      mounted = false;
    };
  }, [countryCode, isRedeemMode, reloadlyAccessToken]);

  const getCurrentPlayer = async () => {
    try {
      setIsLoading(true);
      const res1 = await axiosConn.get(
        `players/?playerId=${player.data.player.playerId}`,
        {
          headers: {
            Authorization: `Bearer ${player.token}`,
          },
        },
      );
      setCurrPlayer(res1.data.data.data[0]);
      setIsLoading(false);
    } catch (err) {
      console.log(err);
      setIsLoading(false);
    }
  };

  const onSuccess = (data) => {
    // console.log('data: ', data);
  };

  const onError = (err) => {
    // console.log('err: ', err);
  };

  const { data, status } = useGetCurrPlayer(onSuccess, onError, player);
  // console.log(data);

  useEffect(() => {
    let mounted = true;
    //set conversations
    if (status === 'success') {
      if (!mounted) return;
      setCurrPlayer(data);
    }
    return () => {
      mounted = false;
    };
  }, [status, data]);

  useEffect(() => {
    let mounted = true;
    //This grabs logged on player's friends.
    const getMyFriends = async () => {
      try {
        setFriendsLoading(true);
        const friendList = await axiosConn.get(
          'players/friends/' + player.data.player._id,
          {
            headers: {
              Authorization: `Bearer ${player.token}`,
            },
          },
        );
          if (!mounted) return;
        const safeFriends = Array.isArray(friendList.data)
          ? friendList.data.filter((f) => f && f._id)
          : [];
        if (safeFriends.length !== (friendList.data?.length || 0)) {
          console.warn('[CashoutJGKToken] filtered stale/null friends', {
            requested: friendList.data?.length || 0,
            kept: safeFriends.length,
          });
        }
        SetMyFriends(safeFriends);
        setFriendsLoading(false);
      } catch (err) {
        console.log(err);
        setFriendsLoading(false);
      }
    };
    getMyFriends();
    return () => {
      mounted = false;
    };
  }, [player.token, player.data.player._id]);

  const filteredGiftCards = useMemo(() => {
    if (!giftSearch) return giftCards;
    const query = giftSearch.toLowerCase();
    return giftCards.filter((giftCard) =>
      giftCard.productName?.toLowerCase().includes(query),
    );
  }, [giftCards, giftSearch]);

  const filteredFriends = useMemo(() => {
    if (!friendSearch) return myFriends;
    const query = friendSearch.toLowerCase();
    return myFriends.filter((friend) => {
      const display = `${friend.playerId || ''} ${friend.displayName || ''}`;
      return display.toLowerCase().includes(query);
    });
  }, [myFriends, friendSearch]);

  const sortedFriends = useMemo(() => {
    return [...filteredFriends].sort((a, b) => {
      const aScore = Number(a?.jgkTokens ?? a?.jgkToken ?? 0);
      const bScore = Number(b?.jgkTokens ?? b?.jgkToken ?? 0);
      if (bScore !== aScore) return bScore - aScore;
      const aId = a?.playerId || '';
      const bId = b?.playerId || '';
      return aId.localeCompare(bId);
    });
  }, [filteredFriends]);

  const visibleFriends = useMemo(
    () => sortedFriends.slice(0, visibleFriendCount),
    [sortedFriends, visibleFriendCount],
  );

  const visibleGiftCards = useMemo(
    () => filteredGiftCards.slice(0, visibleGiftCount),
    [filteredGiftCards, visibleGiftCount],
  );

  const selectedFriend = useMemo(() => {
    if (!selectedPlayer || selectedPlayer === 'Choose a Player') return null;
    return (
      myFriends.find((friend) => friend.playerId === selectedPlayer) || null
    );
  }, [myFriends, selectedPlayer]);

  const quickSend = useMemo(() => {
    if (!lastSend?.recipient || !lastSend?.amount) return null;
    const friend = myFriends.find(
      (entry) => entry.playerId === lastSend.recipient,
    );
    if (!friend) return null;
    return {
      friend,
      amount: lastSend.amount,
    };
  }, [lastSend, myFriends]);

  const handleGiftCardPick = (giftCard) => {
    if (!giftCard) return;
    setThisGiftCard(giftCard);
    setActive('');
    setGiftCardUSDAmount(0);
    setCashoutGiftCardAmount(0);
    SetError('');
  };

  const handleClickGiftCard = () => {
    // console.log(event);
    cashoutGiftCard === 0 ? setCashoutGiftCard(1) : setCashoutGiftCard(0);
    // console.log(cashoutGiftCard);
    if (cashoutGiftCard === 0) {
      setMessage('');
      SetError('');
      setCashoutGiftCardAmount(0);
      setActive('');
      setGiftCardUSDAmount(0);
      setThisGiftCard('');
    }
  };

  const handleSendJGKPlayer = () => {
    // console.log(event);
    sendJGKPlayer === 0 ? setSendJGKPlayer(1) : setSendJGKPlayer(0);
    // console.log(sendJGKPlayer);
    if (sendJGKPlayer === 0) {
      setMessage('');
      SetError('');
      setSelectedPlayer('Choose a Player');
      setSendJGKToPlayerAmount(0);
    }
  };

  const handleCashoutGiftCardAmount = async (e = {}) => {
    const eventKey = e?.key;
    const eventType = e?.type;
    // console.log(
    //   e.key,
    //   e.type,
    //   cashoutGiftCardAmount,
    //   selectedGiftCard,
    //   e.target.value,
    // );
    if (isSuspended) {
      SetError('You are temporarily suspended from making transactions.');
      setIsLoading(false);
      return;
    }

    //reset error backspacing to fix amount.
    if (eventKey === undefined && error !== '') {
      // console.log(e.key, 'resetting Error', error);
      SetError('');
    }

    //3% JGK transaction fee.
    // 1 JGK = .001 USD current conversion
    // thisGiftCard.discountPercentage flowers 6%
    //maxRecipientDenomination: flowers 100.
    //senderFee flowers .5
    //rounding and max between cashoutGiftCardAmount & maxRecipientDenomination.
    //settting USD Amount
    if (thisGiftCard.denominationType === 'RANGE') {
      setGiftCardUSDAmount(
        // parseFloat(cashoutGiftCardAmount * 0.97 * 0.01).toFixed(2),
        parseFloat(cashoutGiftCardAmount * usdJGKDecimalConversionRate).toFixed(
          2,
        ),
      );
    } else if (thisGiftCard.denominationType === 'FIXED') {
      // console.log(cashoutGiftCardAmount, giftCardUSDAmount);
    }

    if (eventKey === 'Enter' || eventType === 'click') {
      // console.log('handleCashoutGiftCardAmount ordering');
      // console.log(
      //   player.data.player._id,
      //   cashoutGiftCardAmount,
      //   thisGiftCard.productName,
      // );
      // console.log(
      //   giftCardUSDAmount,
      //   thisGiftCard,
      //   currPlayer.jgkTokens,
      // );

      if (
        thisGiftCard.denominationType === 'RANGE' &&
        (giftCardUSDAmount < thisGiftCard.minSenderDenomination ||
          giftCardUSDAmount > thisGiftCard.maxSenderDenomination ||
          giftCardUSDAmount +
            thisGiftCard?.senderFee * usdJGKConversionRate +
            cashoutGiftCardAmount * jgkTransactionFee >
            currPlayer?.jgkTokens)
      ) {
        // console.log(giftCardUSDAmount, cashoutGiftCardAmount, thisGiftCard);
        SetError(
          `${thisGiftCard.productName} min: $${thisGiftCard.minSenderDenomination} and max: $${thisGiftCard.maxSenderDenomination}. Please check available JGK, minimum and maximum, adjust Amount and try again.`,
        );
        return;
      } else if (
        thisGiftCard.denominationType === 'FIXED' &&
        giftCardUSDAmount +
          thisGiftCard?.senderFee * usdJGKConversionRate +
          cashoutGiftCardAmount * jgkTransactionFee >
          currPlayer?.jgkTokens
      ) {
        SetError(`Please check available JGK, adjust Amount and try again.`);
        return;
      }

      setIsLoading(true);
      try {
        // console.log(`${player.data.player.playerId}${new Date().getTime()}`);

        await axiosConn.post(
          `/jgkTokenTransactions/jgkCashoutToGiftCard`,
          {
            sender: player.data.player._id,
            amount: cashoutGiftCardAmount,
            giftCard: thisGiftCard.productName,
            productId: thisGiftCard.productId,
            countryCode: thisGiftCard.countryCode,
            quantity: 1,
            unitPrice: giftCardUSDAmount,
          },
          {
            headers: {
              Authorization: `Bearer ${player.token}`,
            },
          },
        );
        setIsLoading(false);
        setMessage(
          `Success! Your ${thisGiftCard.productName} gift card has been ordered. Check your email!`,
        );
        setCashoutGiftCardAmount(0);
        setGiftCardUSDAmount(0);
        setActive('');
        queryClient.invalidateQueries({ queryKey: ['getCurrPlayer'] });
        queryClient.invalidateQueries({ queryKey: ['jgkTransactions'] });
        if (onRedeemSuccess) onRedeemSuccess();
      } catch (err) {
        console.log(err);
        SetError(err.response.data.message);
        setIsLoading(false);
        // console.log(err);
      }
    }
  };

  const handleDenominationButtonClick = (e) => {
    // console.log(
    //   'handleDenominationButtonClick',
    //   e.target.value,
    //   e.target.id,
    //   parseFloat(e.target.value) * 100,
    // );
    setActive(e.target.id);
    setGiftCardUSDAmount(parseFloat(e.target.value));

    setCashoutGiftCardAmount(parseFloat(e.target.value) * 100);

    //so we'll have the current jgkTokens for Ordering.
    //case a player sends JGK just before ordering.
    getCurrentPlayer();
    // console.log(currPlayer.jgkTokens);
  };

  const handleSendJGKToPlayerAmount = async (e = {}) => {
    const eventKey = e?.key;
    const eventType = e?.type;
    if (eventKey === 'Enter' || eventType === 'click') {
      setIsLoading(true);

      const receiverEntry = myFriends.find(
        (f) => selectedPlayer === f.playerId,
      );
      const amountValue = Number(sendJGKToPlayerAmount);

      if (!receiverEntry) {
        SetError('Choose a player to send JGK.');
        setIsLoading(false);
        return;
      }

      if (!Number.isFinite(amountValue) || amountValue <= 0) {
        SetError('Enter an amount greater than 0.');
        setIsLoading(false);
        return;
      }

      if (isSuspended) {
        SetError('You are temporarily suspended from making transactions.');
        setIsLoading(false);
        return;
      }

      // Check if the sender has enough jgkTokens
      if (currPlayer.jgkTokens < amountValue) {
        setIsLoading(false);
        console.log('Insufficient JGK tokens');
        // Optionally, set an error message to display to the user
        SetError('Insufficient JGK tokens');
        return;
      }

      try {
        await axiosConn.post(
          `jgkTokenTransactions/`,
          {
            receiver: receiverEntry._id,
            sender: player.data.player._id,
            amount: amountValue,
            text: sendJGKToPlayerMemo,
          },
          {
            headers: {
              Authorization: `Bearer ${player.token}`,
            },
          },
        );
        setIsLoading(false);
        setMessage(`Success! JGK sent to ${selectedPlayer}.`);
        setSendJGKToPlayerAmount(0);
        setSendJGKToPlayerMemo('sharing JGK is caring.');
        if (typeof window !== 'undefined') {
          localStorage.setItem('jgkWalletLastSendRecipient', selectedPlayer);
          localStorage.setItem(
            'jgkWalletLastSendAmount',
            String(sendJGKToPlayerAmount),
          );
          setLastSend({
            recipient: selectedPlayer,
            amount: Number(sendJGKToPlayerAmount),
          });
        }
        queryClient.invalidateQueries({ queryKey: ['getCurrPlayer'] });
        queryClient.invalidateQueries({ queryKey: ['jgkTransactions'] });
        if (onSendSuccess) onSendSuccess();
      } catch (err) {
        setIsLoading(false);
        console.log(err);
        // Optionally, set an error message to display to the user
        SetError('Failed to send JGK tokens. Please try again.');
      }
    }
  };

  const handleHelpIconClick = (event) => {
    // console.log('handleHelpIconClick()');
    SetShowJGKTutorial(showJGKTutorial === true ? false : true);
  };

  const canReviewRedeem = Boolean(thisGiftCard) && cashoutGiftCardAmount > 0;
  const giftCardLogo = thisGiftCard?.logoUrls?.[0];
  const conversionLabel = `${usdJGKConversionRate} JGK = $1`;

  return (
    <div
      className={`jgk-wallet-panel ${
        embedded ? 'jgk-wallet-panel--embedded' : ''
      } ${darkTheme ? 'jgk-wallet-panel--dark' : ''}`}
    >
      {!embedded && (
        <div className="jgk-wallet-panel__header">
          <DisplayPlayerJGK />
        </div>
      )}

      {showModeToggle && (
        <div className="jgk-wallet-panel__toggles">
          <Form>
            <div className="jgk-wallet-panel__toggle">
              <Form.Check
                type="checkbox"
                id="cashoutGiftCard"
                checked={Boolean(cashoutGiftCard)}
                label="JGK to Gift Card"
                onChange={handleClickGiftCard}
                disabled={sendJGKPlayer ? true : false}
              />
              <Tooltip
                title={
                  showJGKTutorial === false
                    ? 'Click and watch JGK to Gift Card video'
                    : 'Click to close tutorial video'
                }
                arrow
              >
                <HelpOutlineOutlinedIcon
                  fontSize="small"
                  sx={
                    showJGKTutorial === true
                      ? { color: '#450b7a' }
                      : { color: '#8a2be2' }
                  }
                  onClick={handleHelpIconClick}
                />
              </Tooltip>
            </div>
            <div className="jgk-wallet-panel__toggle">
              <Form.Check
                type="checkbox"
                checked={Boolean(sendJGKPlayer)}
                id="sendJGKPlayer"
                label="Send JGK to Friends"
                onChange={handleSendJGKPlayer}
                disabled={cashoutGiftCard ? true : false}
              />
            </div>
          </Form>
        </div>
      )}

      {cashoutGiftCard ? (
        <div className="jgk-wallet-redeem">
          <div className="jgk-wallet-redeem__search">
            <input
              type="text"
              value={giftSearch}
              placeholder="Search gift cards"
              onChange={(e) => setGiftSearch(e.target.value)}
            />
          </div>
          <div className="jgk-wallet-redeem__grid">
            {giftCardsLoading
              ? Array.from({ length: 6 }).map((_, index) => (
                  <div
                    key={`giftcard-skeleton-${index}`}
                    className="jgk-wallet-skeleton-tile"
                  />
                ))
              : null}
            {!giftCardsLoading && filteredGiftCards.length === 0 && (
              <div className="jgk-wallet__empty">No gift cards found.</div>
            )}
            {!giftCardsLoading &&
              visibleGiftCards.map((giftCard) => {
                const isActive =
                  thisGiftCard?.productId === giftCard.productId ||
                  thisGiftCard?.productName === giftCard.productName;
                return (
                  <button
                    type="button"
                    key={giftCard.productId || giftCard.productName}
                    className={`jgk-wallet-redeem__tile ${
                      isActive ? 'is-active' : ''
                    }`}
                    onClick={() => handleGiftCardPick(giftCard)}
                  >
                    <img
                      src={giftCard.logoUrls?.[0]}
                      alt=""
                      className="jgk-wallet-redeem__tile-logo"
                      loading="lazy"
                    />
                    <div className="jgk-wallet-redeem__tile-title">
                      {giftCard.productName}
                    </div>
                    {giftCard.discountPercentage ? (
                      <div className="jgk-wallet-redeem__tile-sub">
                        {`Bonus ${giftCard.discountPercentage}%`}
                      </div>
                    ) : null}
                  </button>
                );
              })}
          </div>
          {!giftCardsLoading && filteredGiftCards.length > 0 && (
            <div className="jgk-wallet-list-controls">
              <span className="jgk-wallet-list-count">
                {`Showing ${Math.min(
                  visibleGiftCount,
                  filteredGiftCards.length,
                )} of ${filteredGiftCards.length}`}
              </span>
              {visibleGiftCount < filteredGiftCards.length ? (
                <button
                  type="button"
                  className="jgk-wallet-list-button"
                  onClick={() =>
                    setVisibleGiftCount((prev) => prev + 12)
                  }
                >
                  Load more
                </button>
              ) : null}
            </div>
          )}
          {thisGiftCard ? (
            <div className="jgk-wallet-redeem__details">
              <div className="jgk-wallet-redeem__summary">
                <img
                  src={giftCardLogo}
                  alt=""
                  className="jgk-wallet-redeem__summary-logo"
                  loading="lazy"
                />
                <div>
                  <div className="jgk-wallet-redeem__summary-title">
                    {thisGiftCard.productName}
                  </div>
                  <div className="jgk-wallet-redeem__summary-sub">
                    {thisGiftCard.countryCode} gift card
                  </div>
                </div>
              </div>
              <div className="jgk-wallet-redeem__amount">
                {thisGiftCard?.denominationType === 'RANGE' && (
                  <div className="jgk-wallet-redeem__input">
                    <label htmlFor="giftCardAmount">JGK amount</label>
                    <input
                      type="number"
                      id="giftCardAmount"
                      name="giftCardAmount"
                      value={cashoutGiftCardAmount}
                      onChange={(e) => setCashoutGiftCardAmount(e.target.value)}
                      onInput={handleCashoutGiftCardAmount}
                      onKeyUp={handleCashoutGiftCardAmount}
                    />
                  </div>
                )}
                {thisGiftCard?.denominationType === 'FIXED' && (
                  <div className="jgk-wallet-redeem__fixed">
                    {thisGiftCard.fixedRecipientDenominations.map(
                      (denomination) => (
                        <button
                          type="button"
                          key={denomination}
                          className={`jgk-wallet-redeem__fixed-option ${
                            parseFloat(active) === denomination
                              ? 'is-active'
                              : ''
                          }`}
                          onClick={(e) => handleDenominationButtonClick(e)}
                          id={denomination}
                          value={denomination}
                          disabled={
                            currPlayer.jgkTokens / 100 < denomination
                              ? 'disabled'
                              : null
                          }
                        >
                          {denomination}
                        </button>
                      ),
                    )}
                  </div>
                )}
                {giftCardUSDAmount > 0 && (
                  <div className="jgk-wallet-redeem__conversion">
                    {`About $${giftCardUSDAmount}`}
                  </div>
                )}
              </div>
              <div className="jgk-wallet-redeem__actions">
                <button
                  type="button"
                  className="jgk-wallet-redeem__cta"
                  disabled={!canReviewRedeem || isLoading}
                  onClick={() => setConfirmRedeemOpen(true)}
                >
                  {isLoading ? (
                    <CircularProgress color="inherit" size="20px" />
                  ) : (
                    'Review redemption'
                  )}
                </button>
                <div className="jgk-wallet-redeem__note">{conversionLabel}</div>
              </div>
              {error && <div className="jgk-wallet__error">{error}</div>}
              {message && <div className="jgk-wallet__success">{message}</div>}
            </div>
          ) : (
            <div className="jgk-wallet__empty">
              Select a gift card to continue.
            </div>
          )}
        </div>
      ) : null}

      {sendJGKPlayer ? (
        <div className="jgk-wallet-send">
          {quickSend ? (
            <button
              type="button"
              className="jgk-wallet-send__quick"
              onClick={() => {
                setSelectedPlayer(quickSend.friend.playerId);
                setSendJGKToPlayerAmount(quickSend.amount);
              }}
            >
              <img
                src={
                  quickSend.friend.profilePhoto ||
                  '/assets/person/noAvatar.png'
                }
                alt=""
                className="jgk-wallet-send__quick-avatar"
                loading="lazy"
              />
              <div>
                <div className="jgk-wallet-send__quick-title">Quick send</div>
                <div className="jgk-wallet-send__quick-sub">
                  {`${quickSend.amount} JGK to ${quickSend.friend.playerId}`}
                </div>
              </div>
            </button>
          ) : null}
          <div className="jgk-wallet-send__search">
            <input
              type="text"
              value={friendSearch}
              placeholder="Search friends"
              onChange={(e) => setFriendSearch(e.target.value)}
            />
          </div>
          <div className="jgk-wallet-send__grid">
            {friendsLoading
              ? Array.from({ length: 6 }).map((_, index) => (
                  <div
                    key={`friend-skeleton-${index}`}
                    className="jgk-wallet-skeleton-tile"
                  />
                ))
              : null}
            {!friendsLoading && filteredFriends.length === 0 && (
              <div className="jgk-wallet__empty">No friends found.</div>
            )}
            {!friendsLoading &&
              visibleFriends.map((friend) => {
                const isActive = friend.playerId === selectedPlayer;
                return (
                  <button
                    type="button"
                    key={friend._id || friend.playerId}
                    className={`jgk-wallet-send__friend ${
                      isActive ? 'is-active' : ''
                    }`}
                    onClick={() => setSelectedPlayer(friend.playerId)}
                  >
                    <img
                      src={
                        friend.profilePhoto ||
                        '/assets/person/noAvatar.png'
                      }
                      alt=""
                      className="jgk-wallet-send__avatar"
                      loading="lazy"
                    />
                    <div className="jgk-wallet-send__name">
                      {friend.displayName || friend.playerId}
                    </div>
                    <div className="jgk-wallet-send__id">{friend.playerId}</div>
                  </button>
                );
              })}
          </div>
          {!friendsLoading && filteredFriends.length > 0 && (
            <div className="jgk-wallet-list-controls">
              <span className="jgk-wallet-list-count">
                {`Showing ${Math.min(
                  visibleFriendCount,
                  filteredFriends.length,
                )} of ${filteredFriends.length}`}
              </span>
              {visibleFriendCount < filteredFriends.length ? (
                <button
                  type="button"
                  className="jgk-wallet-list-button"
                  onClick={() =>
                    setVisibleFriendCount((prev) => prev + 16)
                  }
                >
                  Load more
                </button>
              ) : null}
            </div>
          )}
          <div className="jgk-wallet-send__form">
            <div className="jgk-wallet-send__selected">
              {selectedFriend
                ? `Sending to ${selectedFriend.playerId}`
                : 'Choose a friend to send JGK.'}
            </div>
            <div className="jgk-wallet-send__field">
              <label htmlFor="sendJGKToPlayerAmount">Amount (JGK)</label>
              <input
                type="number"
                value={sendJGKToPlayerAmount}
                id="sendJGKToPlayerAmount"
                onChange={(e) => setSendJGKToPlayerAmount(e.target.value)}
                onKeyUp={handleSendJGKToPlayerAmount}
                name="sendJGKToPlayerAmount"
              />
            </div>
            <div className="jgk-wallet-send__field">
              <label htmlFor="sendJGKToPlayerAmountMemo">Memo</label>
              <input
                type="text"
                value={sendJGKToPlayerMemo}
                id="sendJGKToPlayerAmountMemo"
                onChange={(e) => setSendJGKToPlayerMemo(e.target.value)}
                onKeyUp={handleSendJGKToPlayerAmount}
                name="sendJGKToPlayerAmountMemo"
                maxLength={512}
              />
            </div>
            <div className="jgk-wallet-send__actions">
              <button
                type="button"
                className="jgk-wallet-send__cta"
                value="Send"
                onClick={handleSendJGKToPlayerAmount}
                disabled={isLoading}
              >
                {isLoading ? (
                  <CircularProgress color="inherit" size="20px" />
                ) : (
                  'Send JGK'
                )}
              </button>
              <span className="jgk-wallet-send__fee">3% JGK fee</span>
            </div>
            {error && <div className="jgk-wallet__error">{error}</div>}
            {message && <div className="jgk-wallet__success">{message}</div>}
          </div>
        </div>
      ) : null}

      {confirmRedeemOpen && (
        <div className="jgk-wallet-modal">
          <div
            className="jgk-wallet-modal__backdrop"
            onClick={() => setConfirmRedeemOpen(false)}
          />
          <div className="jgk-wallet-modal__card">
            <div className="jgk-wallet-modal__title">Confirm redemption</div>
            <div className="jgk-wallet-modal__row">
              <span>Gift card</span>
              <span>{thisGiftCard?.productName || '-'}</span>
            </div>
            <div className="jgk-wallet-modal__row">
              <span>JGK spend</span>
              <span>{`${cashoutGiftCardAmount || 0} JGK`}</span>
            </div>
            <div className="jgk-wallet-modal__row">
              <span>Approx USD</span>
              <span>{giftCardUSDAmount ? `$${giftCardUSDAmount}` : '$0.00'}</span>
            </div>
            <div className="jgk-wallet-modal__note">{conversionLabel}</div>
            <div className="jgk-wallet-modal__actions">
              <button
                type="button"
                className="jgk-wallet-modal__cancel"
                onClick={() => setConfirmRedeemOpen(false)}
              >
                Cancel
              </button>
              <button
                type="button"
                className="jgk-wallet-modal__confirm"
                onClick={() => {
                  setConfirmRedeemOpen(false);
                  handleCashoutGiftCardAmount({ type: 'click' });
                }}
              >
                Confirm
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
